import { useCallback, useRef, useState } from "react";
import { toastError, toastInfo, toastSuccess } from "@/lib/toast";

// Refresh several useApiData sources together, and SAY SO.
//
// The Refresh buttons used to fire their reloads and return immediately —
// nothing awaited them, no control disabled, no message. Since a refetch usually
// returns the same numbers, the screen was pixel-identical before and after, so
// a working button was indistinguishable from one wired to nothing. Testers
// reported it as dead more than once.
//
// `reloaders` is a list of thunks (typically `source.reload`). useApiData's
// loader resolves to its data on success and to null on failure — including the
// network-down case, where it swaps in demo fixtures — so a null here honestly
// means "this section did not get fresh data".
export function useRefreshAll(reloaders, { label = "Data" } = {}) {
  // Held in a ref so callers can pass a fresh array literal each render without
  // rebuilding the callback.
  const reloadersRef = useRef(reloaders);
  reloadersRef.current = reloaders;

  // A ref, not the state below, guards re-entry: reading `refreshing` from the
  // callback's closure would see the value captured at its last render and let a
  // double click start a second round.
  const busyRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setRefreshing(true);
    try {
      const results = await Promise.all(
        reloadersRef.current.map((run) =>
          // A thunk that throws must not abort its siblings, or one bad section
          // leaves the rest stale with no explanation.
          Promise.resolve().then(run).catch(() => null)
        )
      );
      const failed = results.filter((r) => r == null).length;
      if (failed === 0) {
        toastSuccess(`${label} refreshed.`);
      } else if (failed === results.length) {
        toastError(`Couldn't refresh ${label.toLowerCase()} — the server didn't respond.`);
      } else {
        toastInfo(`${label} refreshed — ${failed} of ${results.length} sections couldn't update.`);
      }
    } finally {
      busyRef.current = false;
      setRefreshing(false);
    }
  }, [label]);

  return { refreshing, refresh };
}
